import type { Bilingual, LandingMarketRoute, MarketingLocale } from './marketingLocale'
import { LANDING_HTML_LANG } from './marketingLocale'
import type { HreflangAlternate } from './seo'
// The LIGHT registry, not content/comparison's index: this module is imported
// by chrome on the app's critical path, and the index eagerly bundles all four
// comparison articles (D1 perf sweep, 2026-08-20).
import { COMPARISON_REGISTRY } from '../content/comparison/registry'

/**
 * Every public marketing document that exists as a pair of per-language URLs.
 *
 * The rule for these pages is: **the URL decides the language, nothing else.**
 * They used to render whichever language the visitor's UI locale happened to be,
 * which gave one URL two bodies of text. That is unindexable (Google sees one of
 * the two, at random, and cannot be pointed at the other), unlinkable (there is
 * no way to send a German journalist the German press page) and it made the
 * language depend on a member's admin `uiLocale` — a value a public page has no
 * business reading, let alone writing.
 *
 * So each document is two URLs, and the marketing language is a prop the ROUTE
 * passes in. No `useMarketingLocale()`, no `i18n.changeLanguage()`, no
 * localStorage: a visitor switching the marketing page to German must not
 * silently re-language the app for the studio member who happens to be logged in
 * in the same browser.
 *
 * The comparison pairs are read out of the WS4 registry rather than restated
 * here, so a new comparison page is registered in exactly one place.
 */

/**
 * The five standalone documents. English keeps the canonical path it has always
 * had — those URLs are in pitch emails, in a DPA and on printed cards — and
 * German lives under an explicit `/de` prefix rather than a translated slug,
 * because unlike the comparison pages these are not search-landing pages whose
 * URL has to read like the query.
 *
 * /terms and /privacy joined the registry on 2026-09-09, when the legal pages
 * went bilingual. Their English paths are unchanged, so every existing link,
 * signup flow and DPA reference still resolves; the German halves are new URLs.
 * See `content/legalCopy.ts` for why the "English only" decision reversed.
 */
export const MARKETING_DOCUMENT_PATHS: Record<
  'press' | 'subprocessors' | 'founding' | 'terms' | 'privacy',
  Bilingual<string>
> = {
  press: { en: '/press', de: '/de/press' },
  subprocessors: { en: '/legal/subprocessors', de: '/de/legal/subprocessors' },
  founding: { en: '/founding', de: '/de/founding' },
  terms: { en: '/terms', de: '/de/terms' },
  privacy: { en: '/privacy', de: '/de/privacy' },
}

/**
 * All pairs — documents plus comparison pages.
 *
 * Deduplicated by English path because two content modules describe the same
 * pair from either end, and the switcher wants one entry per pair.
 */
export const MARKETING_PAGE_PAIRS: Bilingual<string>[] = [
  ...Object.values(MARKETING_DOCUMENT_PATHS),
  ...Array.from(
    new Map(COMPARISON_REGISTRY.map((entry) => [entry.alternates.en, entry.alternates])).values(),
  ),
]

/**
 * Market-targeted landing URLs (decision D26, RB-12 C4): the landing page with
 * its language forced by the route, prerendered and indexable in its own right.
 *
 * `/hu` is the first. It exists so Google can rank a Hungarian page for
 * Hungarian queries, which the single-URL `/welcome` — English to every
 * crawler — never can be. The router, the prerender catalogue, the sitemap
 * test and the hreflang set below all read this list, so a second market is
 * one entry here plus its copy.
 */
export const MARKET_LANDINGS: readonly LandingMarketRoute[] = [
  { path: '/hu', locale: 'hu' },
] as const

/**
 * The hreflang set the landing carries — the SAME set on `/` and on every
 * market URL, self-reference included, because reciprocity is what Google
 * verifies and a one-way annotation is dropped whole.
 *
 * `/` is `x-default` and NOT `en`: it is the page for a visitor whose language
 * has no URL of its own, and it adapts to that visitor's language. Claiming
 * `en` for it would tell Google an adaptive page is an English one.
 */
export const LANDING_HREFLANG: HreflangAlternate[] = [
  ...MARKET_LANDINGS.map((m) => ({ hreflang: LANDING_HTML_LANG[m.locale], href: m.path })),
  { hreflang: 'x-default', href: '/' },
]

/** Trailing slashes are equivalent URLs; the registry stores the bare form. */
function normalise(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.replace(/\/+$/, '') : path
}

/** The pair a URL belongs to, or undefined for a page that is a single URL. */
export function findMarketingPair(path: string): Bilingual<string> | undefined {
  const p = normalise(path)
  return MARKETING_PAGE_PAIRS.find((pair) => pair.en === p || pair.de === p)
}

/** Which language a marketing URL serves, judged by the URL alone. */
export function marketingLocaleOfPath(path: string): MarketingLocale | undefined {
  const pair = findMarketingPair(path)
  if (!pair) return undefined
  return pair.de === normalise(path) ? 'de' : 'en'
}

/**
 * The URL of a marketing document in a given language.
 *
 * Takes the English path as the key because that is the canonical one and the
 * one every call site already has hard-coded. Unknown paths pass through
 * unchanged so a link into a single-URL page (`/welcome#pricing`, `/guide`)
 * stays exactly what it was.
 */
export function marketingPath(path: string, locale: MarketingLocale): string {
  return findMarketingPair(path)?.[locale] ?? path
}

/**
 * Site-root-relative path of a page's Open Graph image.
 *
 * Derived from the URL rather than declared per page, so the PNG a page asks
 * for and the PNG `scripts/generate-og-images.mjs` writes cannot drift apart —
 * they call this same function. `/press` → `/og/press.png`, `/de/legal/
 * subprocessors` → `/og/de-legal-subprocessors.png`.
 *
 * The landing page (and anything else without its own card) gets
 * `/og/default.png`. Every file this returns is COMMITTED under
 * `frontend/public/og/` — the images are regenerated deliberately when a page
 * is added, never on each build, so a broken generator can never ship a preview
 * that 404s.
 *
 * The 21 guide pages share ONE card, `/og/guide.png` — see RB-12 D31. They
 * are reference documentation a reader consults, not a landing page competing
 * on a social-preview headline the way a /compare/* page is, so 21 near-
 * identical PNGs (same wordmark, same layout, one word of title different)
 * would be repo weight bought for a link-preview improvement nobody would
 * notice. Every `/guide/*` path — and the bare `/guide` — resolves here
 * before the general per-path rule below gets a chance to mint 21 files.
 */
export function ogImagePath(path: string): string {
  const p = normalise(path)
  if (p === '/' || p === '/welcome') return '/og/default.png'
  if (p === '/guide' || p.startsWith('/guide/')) return '/og/guide.png'
  return `/og/${p.slice(1).replace(/\//g, '-')}.png`
}
